import { PRIMARY_INDUSTRIES } from '@/constants/industries';
import type { QueryFunction } from '@tanstack/react-query';
import { gridFilters } from '@simplywallst/services';
import {
  formatterCurrency,
  formatterCurrencyTruncated,
  formatterFactor,
  formatterPercentage,
} from '@/utilities/format';
import { getBestValuationRatio } from '@/utilities/getBestValuationRatio';
import type { BestValuationRatio } from '@/utilities/getBestValuationRatio';

export type GridResponseCompanies = Awaited<
  ReturnType<typeof gridFilters>
>['data']['data'][number];
export type GridResponse = Awaited<ReturnType<typeof gridFilters>>['data'];

export const PAGE_SIZE = 24;

export interface Company {
  canonicalUrl: string;
  companyId: string;
  shortDescription: string;
  website: string;
  mainThumb: string;
  exchangeSymbol: string;
  shortName: string;
  yearFounded: string;
  logoUrl: string;
  legalName: string;
  slug: string;
  snowflakePoints: [number, number, number, number, number];
  tickerSymbol: string;
  uniqueSymbol: string;
  return1D: number | null;
  return1DFormatted: string;
  return7D: number | null;
  return7DFormatted: string;
  return1YrAbs: number | null;
  return1YrAbsFormatted: string;
  marketCap: number | null;
  marketCapFormatted: string;
  sharePrice: number | null;
  sharePriceFormatted: string;
  analystsPriceTarget: number | null;
  analystsPriceTargetFormatted: string;
  bestValuationRatio: BestValuationRatio;
  peFormatted: string;
  pbFormatted: string;
  psFormatted: string;
  analystCount: number | null;
  growthEstimate: number | null;
  growthEstimateFormatted: string;
  earningsGrowth: number | null;
  earningsGrowthFormatted: string;
  salesGrowth: number | null;
  salesGrowthFormatted: string;
  dividendYield: number | null;
  dividendYieldFormatted: string;
  industrySlug: string;
  industryLabel: string;
}

export interface Response {
  meta: {
    totalRecords: number;
    totalPages: number;
    realTotalRecords: number;
    state: string;
    noResultIfLimit: boolean;
    return7D: number | null;
    return7DFormatted: string;
    return1YrAbs: number | null;
    return1YrAbsFormatted: string;
    PE: number | null;
    PEFormatted: string;
  };
  companies: Company[];
}

export const companyMapper = (
  company: GridResponseCompanies
): Company | undefined => {
  try {
    const industryId = company.grid.data.primary_industry.id;
    let industrySlug = '';
    let industryLabel = '';
    if (industryId !== null && typeof industryId !== 'undefined') {
      try {
        const industry = PRIMARY_INDUSTRIES[industryId.toString()];
        industrySlug = industry.slug || '';
        industryLabel = industry.label;
      } catch (e) {
        console.warn(e);
      }
    }
    const { score } = company;
    const yearFounded = company.grid.data.year_founded;
    const dividendYield = company.grid.data.dividend_yield;
    // const reportingUnit = company.grid.data.currency_info.reporting_unit || 1;
    const marketCap = company.grid.data.market_cap;
    const currencyISO =
      company.grid.data.currency_info.primary_trading_item_currency_iso;
    const return1D = company.grid.data.return_1d;
    const return7D = company.grid.data.return_7d;
    const return1YrAbs = company.grid.data.return_1yr_abs;
    const analystPriceTarget = company.grid.data.price_target;
    const bestValuationRatio = getBestValuationRatio(
      company.grid.data.pb,
      company.grid.data.pe,
      company.grid.data.price_to_sales,
      industryLabel
    );
    /**
     *
     */
    return {
      canonicalUrl: company.canonical_url,
      companyId: company.company_id,
      shortDescription: company.grid.data.description,
      website: company.info?.data.url,
      mainThumb: company.grid.data.main_thumb,
      exchangeSymbol: company.exchange_symbol,
      shortName: company.name,
      legalName: company.name,
      slug: company.slug,
      snowflakePoints: [
        score.data.value + 1,
        score.data.future + 1,
        score.data.past + 1,
        score.data.health + 1,
        score.data.income + 1,
      ],
      tickerSymbol: company.ticker_symbol,
      uniqueSymbol: company.unique_symbol,
      logoUrl: company.grid.data.logo_url,
      yearFounded: yearFounded === null ? 'n/a' : yearFounded.toString(),
      return1D: return1D,
      return1DFormatted: formatterPercentage(return1D),
      return7D: return7D,
      return7DFormatted: formatterPercentage(return7D),
      return1YrAbs: return1YrAbs,
      return1YrAbsFormatted: formatterPercentage(return1YrAbs),
      marketCap: marketCap,
      marketCapFormatted: formatterCurrencyTruncated(marketCap, currencyISO, 1),
      sharePrice: company.grid.data.share_price,
      sharePriceFormatted: formatterCurrency(
        company.grid.data.share_price,
        currencyISO
      ),
      analystsPriceTarget: analystPriceTarget,
      analystsPriceTargetFormatted: formatterCurrency(
        analystPriceTarget,
        currencyISO
      ),
      bestValuationRatio,
      peFormatted: formatterFactor(company.grid.data.pe),
      pbFormatted: formatterFactor(company.grid.data.pb),
      psFormatted: formatterFactor(company.grid.data.price_to_sales),
      analystCount: company.grid.data.analyst_count,
      growthEstimate: company.grid.data.growth_3y,
      growthEstimateFormatted: formatterPercentage(company.grid.data.growth_3y),
      earningsGrowth: company.grid.data.net_income_growth_annual,
      earningsGrowthFormatted: formatterPercentage(
        company.grid.data.net_income_growth_annual
      ),
      salesGrowth: company.grid.data.revenue_growth_annual,
      salesGrowthFormatted: formatterPercentage(
        company.grid.data.revenue_growth_annual
      ),
      dividendYield: dividendYield,
      dividendYieldFormatted: formatterPercentage(dividendYield),
      industrySlug,
      industryLabel,
    };
  } catch (e) {
    console.warn(e);
  }
};

export const getDefaultResponse = (): Response => ({
  meta: {
    totalRecords: 0,
    totalPages: 0,
    realTotalRecords: 0,
    state: '',
    noResultIfLimit: false,
    return1YrAbs: null,
    return1YrAbsFormatted: 'n/a',
    return7D: null,
    return7DFormatted: 'n/a',
    PE: null,
    PEFormatted: 'n/a',
  },
  companies: [],
});

export const fetchPage: QueryFunction<
  Response,
  readonly [string, string, number],
  number | undefined
> = async ({
  queryKey: [_key, rules, page],
  pageParam = page,
}): Promise<Response> => {
  try {
    const offset = (pageParam - 1) * PAGE_SIZE;
    const response = await gridFilters(
      {
        id: '0',
        offset: offset < 0 ? 0 : offset,
        size: PAGE_SIZE,
        rules,
        state: 'read',
      },
      'grid,score,info'
    );

    const {
      total_records: totalRecords,
      real_total_records: realTotalRecords,
      state,
      noResultIfLimit,
      return_1yr_abs: return1YrAbs = null,
      return_7d: return7d = null,
      pe = null,
    } = response.data.meta;

    const companies = response.data.data
      .map(companyMapper)
      .filter((n): n is Company => typeof n !== 'undefined');

    return {
      meta: {
        totalRecords,
        totalPages: Math.ceil((totalRecords || 0) / PAGE_SIZE),
        realTotalRecords,
        state,
        noResultIfLimit,
        return1YrAbs,
        return1YrAbsFormatted: formatterPercentage(return1YrAbs),
        return7D: return7d,
        return7DFormatted: formatterPercentage(return7d),
        PE: pe,
        PEFormatted: formatterFactor(pe),
      },
      companies,
    };
  } catch (error) {
    console.warn(error);
    return getDefaultResponse();
  }
};
